import type { WorkflowNode } from "@/types/aiToolkit";
import type { WorkflowRunState, NodeOutput } from "@/types/workflowEngine";
import {
  validateHttpConfig,
  interpolateTemplate,
  HTTP_TIMEOUT_MS,
  HTTP_MAX_RESPONSE_CHARS,
} from "./nodePure";

/**
 * Phase 4A.3 — http-request node executor.
 * URL/headers/body support {{variables.x}} / {{nodeOutputs.nodeId.raw}}
 * interpolation. HTTPS-only + private-host block (nodePure.validateHttpConfig),
 * 30s timeout, 1MB response cap. Non-2xx does NOT fail the run — the status
 * lands in parsed.status so a condition node can branch on it; only invalid
 * config or network-level failure throws (-> run failed, consistent with the
 * engine's error contract).
 *
 * NOTE: the engine executes in the browser today, so the target must allow
 * CORS. Server-side execution (plugin-gateway style relaying) is a later
 * milestone; the SSRF guard is already in place for that future.
 */
export async function executeHttpRequestNode(
  node: WorkflowNode,
  state: WorkflowRunState,
): Promise<NodeOutput> {
  const bag = {
    variables: state.variables,
    nodeOutputs: state.nodeOutputs,
    // 4A.5: edge-mapped inputs available as {{inputs.<key>}}
    inputs: state.nodeInputs?.[node.id] ?? {},
  } as unknown as Record<string, unknown>;

  const rawCfg = node.data.httpConfig;
  const interpolated = rawCfg
    ? {
        ...rawCfg,
        url: interpolateTemplate(rawCfg.url ?? "", bag),
        body: rawCfg.body ? interpolateTemplate(rawCfg.body, bag) : undefined,
        headers: rawCfg.headers
          ? Object.fromEntries(
              Object.entries(rawCfg.headers).map(([k, v]) => [k, interpolateTemplate(String(v), bag)]),
            )
          : undefined,
      }
    : undefined;

  const check = validateHttpConfig(interpolated);
  if (!check.ok) {
    throw new Error(`http-request node "${node.data.label || node.id}": ${check.reason}`);
  }

  const method = (interpolated!.method || "GET").toUpperCase();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HTTP_TIMEOUT_MS);
  try {
    const res = await fetch(check.url.toString(), {
      method,
      headers: interpolated!.headers,
      body: method === "GET" ? undefined : interpolated!.body,
      signal: controller.signal,
    });
    let text = await res.text();
    if (text.length > HTTP_MAX_RESPONSE_CHARS) text = text.slice(0, HTTP_MAX_RESPONSE_CHARS);
    let parsedBody: unknown = null;
    try {
      parsedBody = JSON.parse(text);
    } catch {
      parsedBody = null;
    }
    return {
      raw: text,
      parsed: {
        status: res.status,
        ok: res.ok,
        contentType: res.headers.get("content-type"),
        body: parsedBody ?? text.slice(0, 4000),
      },
    };
  } catch (e) {
    if ((e as Error)?.name === "AbortError") {
      throw new Error(`http-request node "${node.data.label || node.id}": timed out after ${HTTP_TIMEOUT_MS / 1000}s`);
    }
    throw new Error(
      `http-request node "${node.data.label || node.id}": ${(e as Error)?.message ?? "network failure"}`,
    );
  } finally {
    clearTimeout(timer);
  }
}
